import { WorkflowRole } from '@exam-management/common/types';
import {
  GetTaskByIdDocument,
  useGetTaskByIdQuery,
  useUpdateAndCreateSubtasksMutation,
  useUpdateTaskAndSubtasksAndCreateSubtasksMutation,
  useUpdateTaskMutation,
  WorkflowCode,
} from '@exam-management/graphql';
import {
  getWorkflowRoleForSubtask,
  getWorkflowRoleForTask,
} from '@exam-management/task-management';
import type { ApolloError } from '@shared/apollo';
import { useAuth } from '@shared/auth';
import { ErrorBox, Loading } from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo } from 'react';
import type { FormState } from 'react-hook-form';

import type { QSetReviewTaskDetailsFormInput } from '../../schemas';
import type { TaskDetails } from '../../types';
import { mapQSetReviewSetSubtaskDetailsFormData, mapTaskDataToTaskDetails } from '../../utils';

export interface QSetReviewTaskContextData {
  mainTaskDetail: TaskDetails;
  assignedRoles: WorkflowRole[];
  submitTaskDetailsForm: (
    formData: QSetReviewTaskDetailsFormInput,
    formState: FormState<QSetReviewTaskDetailsFormInput>,
  ) => Promise<void>;
}

interface QSetReviewTaskProviderProps extends PropsWithChildren {
  taskId: string;
}

export const QSetReviewTaskContext = createContext<QSetReviewTaskContextData | undefined>(
  undefined,
);

export default function QSetReviewTaskProvider(props: Readonly<QSetReviewTaskProviderProps>) {
  const { taskId, children } = props;

  const {
    data,
    loading: isTaskLoading,
    error,
  } = useGetTaskByIdQuery({
    variables: {
      getTaskByIdInput: {
        taskId: taskId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { userProfile } = useAuth();

  const { showSnackbar } = useSnackbar();

  const logger = useLogger();

  const showSuccessSnackbar = (message: string) => {
    showSnackbar({
      message,
      variant: 'success',
    });
  };

  const showErrorSnackbar = (message: string) => {
    showSnackbar({
      message,
      variant: 'error',
    });
  };

  const defaultUpdateTaskMutationOptions = {
    onCompleted: () => {
      showSuccessSnackbar('Update Task Successfully');
    },
    onError: (error: ApolloError) => {
      logger.error(error);
      showErrorSnackbar('Update Task Unsuccessfully');
    },
    refetchQueries: [
      {
        query: GetTaskByIdDocument,
        variables: {
          getTaskByIdInput: {
            taskId: taskId,
          },
        },
      },
    ],
  };

  const [updateTaskAndSubtasksAndCreateSubtasks] =
    useUpdateTaskAndSubtasksAndCreateSubtasksMutation(defaultUpdateTaskMutationOptions);

  const [updateAndCreateSubtasks] = useUpdateAndCreateSubtasksMutation(
    defaultUpdateTaskMutationOptions,
  );

  const [updateTask] = useUpdateTaskMutation(defaultUpdateTaskMutationOptions);

  const mainTaskDetail = useMemo(() => mapTaskDataToTaskDetails(data?.wflGetTaskById.task), [data]);

  const assignedRoles = useMemo(() => {
    const userId = userProfile?.id ?? '';

    const taskRoles = getWorkflowRoleForTask({
      userId,
      assigneeId: mainTaskDetail.assignee.id,
      assignerId: mainTaskDetail.assignerId,
      ownerId: mainTaskDetail.ownerId,
    });

    const subtaskRoles = mainTaskDetail.subtasks.flatMap((subtask) =>
      getWorkflowRoleForSubtask({
        userId,
        assigneeId: subtask.assignee.id,
        assignerId: '',
        parentOwnerId: mainTaskDetail.ownerId,
      }),
    );

    return Array.from(new Set([...taskRoles, ...subtaskRoles]));
  }, [userProfile, mainTaskDetail]);

  const [isOwner, isAssignee] = useMemo(() => {
    return assignedRoles.reduce(
      (acc, role) => [
        acc[0] || role === WorkflowRole.TASK_OWNER,
        acc[1] || role === WorkflowRole.TASK_ASSIGNEE || role === WorkflowRole.SUBTASK_ASSIGNEE,
      ],
      [false, false],
    );
  }, [assignedRoles]);

  const submitTaskDetailsForm = useCallback(
    async (formData: QSetReviewTaskDetailsFormInput) => {
      if (isOwner && isAssignee) {
        await updateTaskAndSubtasksAndCreateSubtasks({
          variables: {
            updateTaskAndSubtasksAndCreateSubtasksInput: {
              mainTaskId: mainTaskDetail?.id ?? '',
              taskAssigneeId: formData.mainTask.assignee.value,
              taskDueDate: formData.mainTask.dueDate,
              taskName: formData.mainTask.name,
              workflowCode: WorkflowCode.ReviewQSet,
              subtasks: [
                ...mapQSetReviewSetSubtaskDetailsFormData(
                  mainTaskDetail.subtasks,
                  formData.subtask,
                ),
              ],
            },
          },
        });
      } else if (isAssignee) {
        await updateAndCreateSubtasks({
          variables: {
            updateAndCreateSubtasksInput: {
              mainTaskId: mainTaskDetail?.id ?? '',
              workflowCode: WorkflowCode.ReviewQSet,
              subtasks: [
                ...mapQSetReviewSetSubtaskDetailsFormData(
                  mainTaskDetail.subtasks,
                  formData.subtask,
                ),
              ],
            },
          },
        });
      } else if (isOwner) {
        await updateTask({
          variables: {
            updateTaskInput: {
              id: mainTaskDetail?.id ?? '',
              name: formData.mainTask.name,
              dueDate: formData.mainTask.dueDate,
              assigneeId: formData.mainTask.assignee.value,
            },
          },
        });
      }
    },
    [updateTaskAndSubtasksAndCreateSubtasks, updateAndCreateSubtasks, assignedRoles],
  );

  const contextValue = useMemo(() => {
    return {
      mainTaskDetail,
      assignedRoles,
      submitTaskDetailsForm,
    };
  }, [taskId, mainTaskDetail, assignedRoles, submitTaskDetailsForm]);

  if (isTaskLoading) {
    return <Loading height='100%' />;
  }

  if (error) {
    return <ErrorBox />;
  }

  return (
    <QSetReviewTaskContext.Provider value={contextValue}>{children}</QSetReviewTaskContext.Provider>
  );
}
